import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import type { FischerPlugin } from '@/lib/fischerPlugin';
import { readLocalStorage, writeLocalStorage } from '@/lib/safeStorage';
import { isInvalidTokenError } from '@/utils/apolloTools';

const storageKey = 'CartPositionsCount';

export type CartLoaderStatus =
  | 'simulating'
  | 'uploading'
  | 'placingOrder'
  | 'deletingProduct'
  | 'addingArticle';

export interface SeefelderCapEntry {
  /** Remaining stock as reported, for messaging. */
  remainingStock: number;
  /** Remaining stock floored to a whole packaging unit; 0 means not orderable. */
  effectiveCap: number;
  /** The line's current quantity, re-registered whenever it changes. */
  quantity: number;
}

export const useCartStore = defineStore('useCartStore', () => {
  const positionsCount = ref<number | null>(null);
  const apollo = ref<any>(null);
  const fischer = ref<FischerPlugin | null>(null);
  const lastModifiedItems = ref<any[]>([]);
  const csvImport = ref<any[]>([]);
  const invalidToken = ref(false);
  const hideManualSimulatePrices = ref(false);
  const freeOfCharge = ref(false);
  const cartLoaderStatus = ref<CartLoaderStatus | null>(null);
  const cartLoaderActive = computed(() => cartLoaderStatus.value !== null);

  // Keyed by articleNo. Rows register facts, not verdicts, so the row error
  // and the checkout gate cannot disagree. An absent entry never blocks.
  const seefelderCaps = ref<Record<string, SeefelderCapEntry>>({});

  const registerSeefelderCap = (articleNo: string, entry: SeefelderCapEntry | null) => {
    if (!articleNo) {
      return;
    }
    if (entry === null) {
      delete seefelderCaps.value[articleNo];
      return;
    }
    seefelderCaps.value[articleNo] = entry;
  };

  const hasBlockingLines = computed(() =>
    Object.values(seefelderCaps.value).some((entry) => entry.quantity > entry.effectiveCap),
  );

  const notifyLocalStorage = () => {
    if (IS_SSR) {
      return;
    }
    const oldValue = Number(readLocalStorage(storageKey));
    if (oldValue !== positionsCount.value) {
      writeLocalStorage(storageKey, String(positionsCount.value));
    }
  };

  const setupLocalStorageEventListener = () => {
    if (IS_SSR) {
      return;
    }
    try {
      window.addEventListener('storage', ({ key, newValue }) => {
        if (key === storageKey) {
          positionsCount.value = Number(newValue);
        }
      });
    } catch (error) {
      if (!IS_SHOWROOM) {
        console.error(error);
      }
    }
  };

  const setPositionsCount = (amount: number | null) => {
    positionsCount.value = amount;
    notifyLocalStorage();
  };

  const refetchPositionsCount = async () => {
    if (IS_SSR) {
      return;
    }

    if (!apollo.value) {
      console.error('No apollo to fetch cart positions count!');
      return;
    }
    const $fischer = fischer.value;

    if (!$fischer?.shop.supportsCart()) {
      return;
    }

    const userId = $fischer.user.id();
    const cartId = $fischer.cartId();
    const accessToken = $fischer.user.accessToken();
    if (!cartId && (userId === 'anonymous' || !accessToken)) {
      return;
    }
    if (cartId && userId === 'anonymous' && !cartId.includes('-')) {
      return;
    }

    const assistedService = $fischer.user.getAssistedService();
    if (assistedService && !assistedService.user) {
      return;
    }
    try {
      const { default: GET_CART_POSITIONS_COUNT } =
        await import('@/graphql/getCartPositionsCount.gql');
      const { data, error } = await apollo.value
        .query(
          GET_CART_POSITIONS_COUNT,
          {
            baseSiteId: $fischer.baseSiteId(),
            accessToken,
            cartId,
            userId,
            division: $fischer.user.division(),
            distributionChannel: $fischer.user.distributionChannel(),
          },
          { requestPolicy: 'network-only' },
        )
        .toPromise();
      if (error) {
        if (isInvalidTokenError(error)) {
          invalidToken.value = true;
        }
        console.error('Error fetching cart positions count', error);
        return;
      }
      const { getCartPositionsCount } = data;
      setPositionsCount(getCartPositionsCount);
    } catch (error: any) {
      if (isInvalidTokenError(error)) {
        invalidToken.value = true;
      }
      console.error('Error fetching cart positions count', error);
    }
  };

  const initialize = async (data: { apollo: any; fischer: any }) => {
    if (apollo.value) {
      return;
    }
    apollo.value = data.apollo;
    fischer.value = data.fischer;

    setupLocalStorageEventListener();
  };

  const setCsvImport = (value: any[]) => {
    csvImport.value = value;
  };

  const setLastModifiedItems = (value: any[]) => {
    lastModifiedItems.value = value;
  };

  const setHideManualSimulatePrices = (value: boolean) => {
    hideManualSimulatePrices.value = value;
  };

  const setFreeOfCharge = (value: boolean) => {
    freeOfCharge.value = value;
  };

  const showCartLoader = (status: CartLoaderStatus) => {
    cartLoaderStatus.value = status;
  };

  const hideCartLoader = () => {
    cartLoaderStatus.value = null;
  };

  return {
    positionsCount,
    lastModifiedItems,
    csvImport,
    invalidToken,
    hideManualSimulatePrices,
    freeOfCharge,
    initialize,
    refetchPositionsCount,
    setPositionsCount,
    setCsvImport,
    setLastModifiedItems,
    setHideManualSimulatePrices,
    setFreeOfCharge,
    cartLoaderStatus,
    cartLoaderActive,
    showCartLoader,
    hideCartLoader,
    seefelderCaps,
    registerSeefelderCap,
    hasBlockingLines,
  };
});
